import React, { useEffect } from "react";
import { connect } from "react-redux";

import SubmitButton from "./SubmitButton";
import GeneralFormError from "./GeneralFormError";
import BalanceInputSection from "./BalanceInputSection";
import PaymentInfoSection from "./PaymentInfoSection";
import ChargedTodaySection from "./ChargedTodaySection";
import SuccessMessage from "./SuccessMessage";
import AddMoreIconsCTA from "./AddMoreIconsCTA";

// Redux action creators
import * as actions from "actions/actions";
// imported separately for clarity because it's not an action creator (but it's in the action creator file)
import { wwwStripeErrorToString as processUnknownErrors } from "actions/actions";

import { INCOMPLETE_FORM_ERROR_MESSAGE } from "./constants";
import {
    formatDollarsToCents,
    validateQuantity,
    purchasePrepaidBalance,
    getSavedCreditCard,
    getCurrentPrepaidBalance
} from "./helpers";
import {
    setSavedCreditCard,
    setQuantityToAddInputError,
    purchasePrepaidBalanceStart,
    purchasePrepaidBalanceFailure,
    purchasePrepaidBalanceSuccess,
    resetToDefault
} from "./actionCreators";

const PrepaidCreditCardForm = ({
    setErrorWithReduxInStripeState,
    stripePublishableKey,
    reduxStripeStateError,
    state,
    dispatch,
    clearErrorsWithReduxInStripeState,
    formHasFieldErrors
}) => {
    // retrieves user's saved credit card info when component mounts
    useEffect(() => {
        getSavedCreditCard().then(savedCreditCardInfo =>
            dispatch(setSavedCreditCard({ savedCreditCardInfo }))
        );
    }, [dispatch]);

    // "form" specifically refers to the form inside of `StripeSplitForm`
    const formIsComplete = () => {
        const hasSavedCard = !!state.savedCreditCardInfo;
        let usingSavedCard = true;
        let formIsEmpty = true;

        if (state.updatingCard || !hasSavedCard) {
            usingSavedCard = false;
        }

        if (!!state.stripeObjectInstance) {
            formIsEmpty = false;
        }

        if (!usingSavedCard && formIsEmpty) {
            return false;
        }

        return true;
    };

    const validateForm = () => {
        let formIsValid = true;

        /* The "quantity to add" input's value and error message
        updates are NOT handled by Redux — they're handled by useReducer.
        If they were handled by Redux, the "quantity to add" error could
        potentally get cleared when it shouldn't be because editing any 
        `StripeSplitForm` input clears all errors in the Redux Stripe state
        automatically.  */
        const { error, isValid: quantityIsValid } = validateQuantity({
            quantityToAdd: state.quantityToAddInput.value
        });

        if (!quantityIsValid) {
            dispatch(setQuantityToAddInputError({ error }));
            formIsValid = false;
        }

        /* The incomplete form error is handled by Redux
        because `StripeSplitForm` needs to know about that error
        in order to for it to be cleared when the user edits a field
        in `StripeSplitForm` after receiving this incomplete form error. */
        if (!formIsComplete()) {
            setErrorWithReduxInStripeState({
                message: INCOMPLETE_FORM_ERROR_MESSAGE
            });

            formIsValid = false;
        }

        return { formIsValid };
    };

    const handleSubmit = e => {
        e.preventDefault();
        if (state.processingRequest) return;

        const { formIsValid } = validateForm();
        if (!formIsValid) return;

        dispatch(purchasePrepaidBalanceStart());

        return purchasePrepaidBalance({
            amountInCents: formatDollarsToCents({
                amount: state.amountToPurchase
            }),
            stripeObjectInstance: state.stripeObjectInstance,
            stripeTokenData: state.stripeTokenData,
            saveCreditCard: state.saveCardInputChecked,
            hasSavedCreditCard: !!state.savedCreditCardInfo,
            updatedCreditCardInfo: state.updatingCard
        })
            .then(handleSubmitSuccess)
            .catch(error => {
                dispatch(purchasePrepaidBalanceFailure());

                // sets unforeseen errors that haven't been accounted for
                const processedError = processUnknownErrors(error);
                setErrorWithReduxInStripeState(processedError);
            });
    };

    const resetPageToDefault = async e => {
        e.preventDefault();

        const currentPrepaidBalance = await getCurrentPrepaidBalance();
        const savedCreditCardInfo = await getSavedCreditCard();

        dispatch(
            resetToDefault({
                currentPrepaidBalance,
                savedCreditCardInfo
            })
        );

        scrollToTop();

        function scrollToTop() {
            window.scrollTo(0, 0);
        }
    };

    const handleSubmitSuccess = () => {
        dispatch(purchasePrepaidBalanceSuccess());
    };

    const formHasErrors =
        reduxStripeStateError ||
        state.quantityToAddInput.error ||
        formHasFieldErrors;

    return (
        <form className="new-form">
            <BalanceInputSection state={state} dispatch={dispatch} />
            <PaymentInfoSection
                state={state}
                dispatch={dispatch}
                stripePublishableKey={stripePublishableKey}
                clearErrorsWithReduxInStripeState={
                    clearErrorsWithReduxInStripeState
                }
            />
            <ChargedTodaySection state={state} />
            <div className="new-form button-container">
                {state.successMessage ? (
                    <div className="success-message-container">
                        <SuccessMessage
                            successMessage={
                                state.balancePurchased && state.successMessage
                            }
                        />{" "}
                        <AddMoreIconsCTA onClick={resetPageToDefault} />
                    </div>
                ) : (
                    <GeneralFormError
                        errorMessage={
                            reduxStripeStateError ||
                            state.quantityToAddInput.error
                        }
                    />
                )}
                <SubmitButton
                    onClick={handleSubmit}
                    text={state.submitButtonText}
                    processing={state.processingRequest}
                    error={formHasErrors}
                    success={state.balancePurchased}
                    disabled={state.balancePurchased || formHasErrors}
                />
            </div>
        </form>
    );
};

const mapStateToProps = state => {
    const formHasFieldErrors = () => {
        if (!state.stripe.fieldErrors) return false;
        return Object.keys(state.stripe.fieldErrors).length > 0;
    };

    return {
        stripePublishableKey: state.user.stripe,
        reduxStripeStateError: state.stripe.error,
        formHasFieldErrors: formHasFieldErrors()
    };
};

const mapDispatchToProps = dispatch => ({
    setErrorWithReduxInStripeState: error =>
        dispatch(actions.stripeCardInputError(error)),
    clearErrorsWithReduxInStripeState: () =>
        dispatch(actions.stripeClearErrors())
});

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(PrepaidCreditCardForm);
